/**
 * Animates circular survey progress rings when they enter the viewport (once per ring).
 * Both the stroke and the percentage number count up together.
 */
(() => {
  const SELECTOR = '[data-survey-progress]';
  const DEFAULT_DURATION_S = 1.1;
  /** @type {WeakSet<HTMLElement>} */
  const initializedRoots = new WeakSet();

  /**
   * Easing function matching the CSS cubic-bezier(0.33, 1, 0.68, 1).
   * @param {number} t - Progress from 0 to 1
   * @returns {number}
   */
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  /**
   * Animates a label element from 0 to the target percentage.
   * @param {HTMLElement} label
   * @param {number} target
   * @param {number} durationMs
   */
  const animateCount = (label, target, durationMs) => {
    if (!label) return;

    let start = 0;

    const step = (timestamp) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const progress = Math.min(elapsed / durationMs, 1);
      const eased = easeOutCubic(progress);
      const current = Math.round(eased * target);

      label.textContent = `${current}%`;

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  };

  /**
   * @param {HTMLElement} root
   */
  const initRoot = (root) => {
    if (!(root instanceof HTMLElement) || initializedRoots.has(root)) return;

    const circle = root.querySelector('.survey-progress-ring__progress');
    if (!(circle instanceof SVGCircleElement)) return;

    const label = root.querySelector('[data-survey-label]');

    initializedRoots.add(root);

    const percentRaw = Number(root.dataset.percent);
    const percent = Math.min(100, Math.max(0, Number.isFinite(percentRaw) ? percentRaw : 0));
    const r = Number(circle.getAttribute('r'));
    if (!Number.isFinite(r) || r <= 0) return;

    const durationRaw = Number(root.dataset.duration);
    const durationS = Number.isFinite(durationRaw) && durationRaw > 0 ? durationRaw : DEFAULT_DURATION_S;
    const durationMs = durationS * 1000;

    root.style.setProperty('--survey-ring-duration', `${durationS}s`);

    const circumference = 2 * Math.PI * r;
    circle.style.strokeDasharray = String(circumference);
    circle.style.strokeDashoffset = String(circumference);

    let completed = false;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || completed) continue;
          completed = true;
          const offset = circumference * (1 - percent / 100);
          circle.style.strokeDashoffset = String(offset);
          animateCount(label, percent, durationMs);
          observer.disconnect();
        }
      },
      { threshold: 0.2, rootMargin: '0px' }
    );

    observer.observe(root);
  };

  const initAll = () => {
    for (const root of document.querySelectorAll(SELECTOR)) {
      if (!(root instanceof HTMLElement)) continue;
      initRoot(root);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
